"use client";

/**
 * Single-date calendar for the public booking page (barbers, salons, etc.).
 * Same MUI DateCalendar as the dashboard widget, with dark mode support.
 * Use for flows that need one date only; property rental keeps check-in/check-out pickers.
 */
import { useState, useEffect, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs, { type Dayjs } from "dayjs";

const FALLBACK_PRIMARY = "#E65C5C";

function readCssColor(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const value = getComputedStyle(document.body).getPropertyValue(name).trim();
  return value || fallback;
}

function useBookingCalendarPalette() {
  const [palette, setPalette] = useState(() => ({
    isDark: false,
    primary: FALLBACK_PRIMARY,
    paper: "#ffffff",
    background: "#ECEFEC",
  }));

  useEffect(() => {
    const read = () => {
      const colorScheme = getComputedStyle(document.body).colorScheme;
      const htmlDark = document.documentElement.classList.contains("dark");
      const isDark = colorScheme === "dark" || (colorScheme !== "light" && htmlDark);
      setPalette({
        isDark,
        primary: readCssColor("--primary", FALLBACK_PRIMARY),
        paper: readCssColor("--card", isDark ? "#1e293b" : "#ffffff"),
        background: readCssColor("--background", isDark ? "#0f172a" : "#ECEFEC"),
      });
    };
    read();
    window.addEventListener("theme-change", read);
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["style", "data-booking-theme"] });
    return () => {
      window.removeEventListener("theme-change", read);
      observer.disconnect();
    };
  }, []);

  return palette;
}

type BookingDateCalendarProps = {
  /** Current value as YYYY-MM-DD */
  value: string;
  onChange: (dateStr: string) => void;
  /** Minimum selectable date (YYYY-MM-DD) */
  minDate?: string;
  /** Disable specific dates (e.g. blocked or fully booked) */
  shouldDisableDate?: (dateStr: string) => boolean;
};

export function BookingDateCalendar({
  value,
  onChange,
  minDate,
  shouldDisableDate,
}: BookingDateCalendarProps) {
  const { isDark, primary, paper, background } = useBookingCalendarPalette();
  const valueDayjs = value ? dayjs(value) : null;
  const minDateDayjs = minDate ? dayjs(minDate) : undefined;

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: primary },
          background: { default: background, paper },
        },
      }),
    [isDark, primary, paper, background]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          value={valueDayjs}
          onChange={(d: Dayjs | null) => onChange(d ? d.format("YYYY-MM-DD") : "")}
          minDate={minDateDayjs}
          showDaysOutsideCurrentMonth
          shouldDisableDate={
            shouldDisableDate
              ? (d: Dayjs) => shouldDisableDate(d.format("YYYY-MM-DD"))
              : undefined
          }
          sx={{
            width: "100%",
            maxWidth: 360,
            "& .MuiPickersDay-root": {
              fontSize: "0.875rem",
            },
            "& .MuiDayCalendar-header": {
              fontSize: "0.75rem",
              color: "var(--muted-foreground)",
            },
          }}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
}
